<template>
  <spinner v-if="isLoading" class="m-y-auto m-x-auto"/>
  <div v-else class="p-1">
    <vk-modal
        v-if="showModal"
        :has-close-icon="false">
      <transition name="fade" mode="out-in">
        <service-unavailable-modal
            :service="service"
            :zipcode="$route.params.zipcode"
            @ok="handleNoPros()"/>
      </transition>
    </vk-modal>
    <div v-else>
      <customer-greeting :name="name"/>
      <next-steps />
      <share-address/>
      <manage-request-tos />
      <pro-licensing />
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import workOrder from '@/mixins/work-order';
import authWithToken from '@/mixins/auth-token';
import { getPRWorkOrderByHash } from '@homeservices/api-kit/workorder/proreferral';
import { getContacts } from '@homeservices/api-kit/security/users';
import { getCities } from '@homeservices/api-kit/search';

import _keyBy from 'lodash/keyBy';
import _get from 'lodash/get';

import CustomerGreeting from '../../components/CustomerGreeting';
import NextSteps from '../../components/NextSteps';
import ManageRequestTos from '../../components/ManageRequestTos';
import ProLicensing from '../../components/ProLicensing';
import ShareAddress from '../../components/ShareAddress';
import ServiceUnavailableModal from '../../../JobRequestView/components/ServiceUnavailableModal';

export default {
  name: 'tos-confirmation',
  components: {
    CustomerGreeting,
    NextSteps,
    ManageRequestTos,
    ProLicensing,
    ShareAddress,
    ServiceUnavailableModal,
  },
  mixins: [
    workOrder,
    authWithToken,
  ],
  data () {
    return {
      isLoading: false,
      phone: '',
      email: '',
      leadHash: '',
      status: '',
      city: '',
      state: '',
      zipcode: '',
      service: {},
    };
  },
  computed: {
    ...mapGetters('user', ['me', 'userId']),
    ...mapGetters('jobrequest/matches', ['selectedPublicProfilesIds']),
    ...mapGetters('jobrequest/overview', ['tosId']),
    token () {
      return this.$route.query.token;
    },
    showModal () {
      return this.selectedPublicProfilesIds && !this.selectedPublicProfilesIds.length;
    },
    name () {
      return this.me.first_name || '';
    },
  },
  async beforeRouteLeave (to, from, next) {
    if (to.name === 'homepage' || to.name === 'login') {
      await this.$store.dispatch('user/logoutAndClearUserSession');
    }
    next();
  },
  async mounted () {
    await this.$store.dispatch('user/logoutAndClearUserSession');
    await this.update();
  },
  methods: {
    async update () {
      this.isLoading = true;
      const { sourceID, lead } = this.$route.query;
      this.leadHash = lead;

      try {
        if (this.leadHash) {
          this.$store.dispatch('jobrequest/clear');
        }
        await this.$store.dispatch('jobrequest/overview/fetchTosId');
        const serviceId = String(this.$route.params.serviceId);
        this.zipcode = String(this.$route.params.zipcode);
        const seed = Math.random().toString(36).substring(7);
        await this.getCityState();

        // fetch service details
        await this.$store.dispatch('jobrequest/fetchService', serviceId);
        await this.$store.dispatch('jobrequest/fetchAvailability', { zipcode: this.zipcode });
        await this.$store.dispatch('jobrequest/matches/fetchPublicProfiles', { serviceId, zipcode: this.zipcode, seed });
        // login with token provided from email. This happens after fetching service details because the token lacks servicecatalog scope
        if (this.isLoggedIn) {
          await this.$store.dispatch('user/logoutAndClearUserSession');
        }
        await this.authenticateWithToken(this.token);
        // gather lead details
        const { data: result } = await getPRWorkOrderByHash(this.leadHash);
        this.$store.dispatch('jobrequest/details/setNotes', result.description || '');
        this.$store.commit('jobrequest/setSourceID', result.click_source || null);
        this.$store.commit('jobrequest/setHDStore', result.hdstore.store_number || '');
        this.$store.commit('jobrequest/setWorkOrderId', this.leadHash);
        this.$store.commit('jobrequest/setAssociateId', result.hdstore.associate_id || '');
        this.service.display_name = result.service_display_name;
        this.status = result.status;

        // gather user details
        await this.fetchContactInformation();
        this.$store.dispatch('jobrequest/overview/setEmail', this.email || '');
        this.$store.dispatch('jobrequest/overview/setFirstName', this.me.first_name || '');
        this.$store.dispatch('jobrequest/overview/setLastName', this.me.last_name || '');
        this.$store.dispatch('jobrequest/overview/setPhone', this.phone || '');
        this.$store.commit('jobrequest/setLocation', { city: this.city, state: this.state });
        this.$store.commit(
          'jobrequest/setSourceID',
          sourceID || 'browse'
        );

        await this.updateUserTos();

        const params = {
          zipcode: this.$route.params.zipcode,
          leadHash: this.$route.query.lead,
          sms: false,
        };
        // need to prevent attempt to update unless status is 'pending_tos_acceptance'
        if (this.status === 'pending_tos_acceptance') {
          await this.$store.dispatch('jobrequest/update', params);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchContactInformation () {
      const { data } = await getContacts({ user_ids: this.userId, contact_types: 'PHONE,EMAIL' });
      const contacts = _keyBy(data.results, 'contact_type');
      this.phone = _get(contacts, 'PHONE.contact', '');
      this.email = _get(contacts, 'EMAIL.contact', '');
    },
    async getCityState () {
      try {
        const response = await getCities('US', this.zipcode);
        if (response.data.results.length) {
          const location = response.data.results[0];
          this.city = location.city;
          this.state = location.state;
        }
      } catch (err) {
        console.error(err);
      }
    },
    handleNoPros () {
      this.$store.dispatch('user/logoutAndClearUserSession');
      this.$router.push({ name: 'homepage' });
    },
    async updateUserTos () {
      const userData = {
        consumer_terms_of_service_id: this.tosId,
      };
      await this.$store.dispatch('user/updateMe', userData);
    },
  },
};
</script>
